/**
 * Cart Persistence Utilities
 * 
 * Handles saving and restoring cart state to/from localStorage
 * with expiration logic and validation.
 */

import type { Cart, RegistrationWizardState } from '@/types';

const CART_STORAGE_KEY = 'cactus_cup_cart';
const CART_EXPIRATION_HOURS = 24;

export interface PersistedCart {
  userId: string;
  cart: Cart;
  wizardState: RegistrationWizardState;
  timestamp: string;
  expiresAt: string;
}

/**
 * Save cart and wizard state to localStorage
 */
export function saveCart(
  userId: string,
  cart: Cart,
  wizardState: RegistrationWizardState
): void {
  try {
    const now = new Date();
    const expiresAt = new Date(now.getTime() + CART_EXPIRATION_HOURS * 60 * 60 * 1000);

    const persistedCart: PersistedCart = {
      userId,
      cart,
      wizardState,
      timestamp: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
    };

    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(persistedCart));
  } catch (error) {
    console.error('Failed to save cart to localStorage:', error);
    // Don't throw - cart persistence is a nice-to-have, not critical
  }
}

/**
 * Load cart from localStorage
 * Returns null if cart doesn't exist, is expired, or belongs to different user
 */
export function loadCart(userId: string): PersistedCart | null {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const persistedCart: PersistedCart = JSON.parse(stored);

    // Check if cart belongs to current user
    if (persistedCart.userId !== userId) {
      clearCart();
      return null;
    }

    // Check if cart has expired
    const expiresAt = new Date(persistedCart.expiresAt);
    const now = new Date();
    if (now > expiresAt) {
      clearCart();
      return null;
    }

    return persistedCart;
  } catch (error) {
    console.error('Failed to load cart from localStorage:', error);
    // Clear corrupted data
    clearCart();
    return null;
  }
}

/**
 * Clear cart from localStorage
 */
export function clearCart(): void {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear cart from localStorage:', error);
  }
}

/**
 * Check if a persisted cart exists for the user
 */
export function hasPersistedCart(userId: string): boolean {
  const cart = loadCart(userId);
  return cart !== null;
}

/**
 * Get the age of the persisted cart in milliseconds
 * Returns null if no cart exists
 */
export function getCartAge(userId: string): number | null {
  const cart = loadCart(userId);
  if (!cart) {
    return null;
  }

  const timestamp = new Date(cart.timestamp);
  const now = new Date();
  return now.getTime() - timestamp.getTime();
}
